import React from 'react';
import SectionHeader from '@components/common/SectionHeader';
import ScoreRadarChart from '@components/charts/ScoreRadarChart';
import { formatNumber, formatUpdateDate } from '@utils/format';
import type { EnterpriseScore, ScoreValue } from '@app-types/performance';

export interface EnterpriseScoreSectionProps {
  data: EnterpriseScore;
  isOpeningStockReport?: boolean;
  onOpenStockReport?: () => Promise<string>;
}

// At the 626px Figma baseline, the max bar is 133px inside a 161px value slot.
// Keep that ratio so bars grow/shrink with the responsive table column.
const SCORE_BAR_MAX_TRACK_PERCENT = (133 / 161) * 100;

// Figma node 3484:25183: dedicated radar drawing area inside the 225px card.
// The legend occupies its own row below this canvas.
const SCORE_RADAR_CANVAS_HEIGHT = 167;

const barWidthPercent = (value: ScoreValue, axisMax: number): number => {
  if (value === null || !Number.isFinite(value)) return 0;
  if (!axisMax || axisMax <= 0) return 0;
  const clamped = Math.max(0, Math.min(value, axisMax));
  return (clamped / axisMax) * SCORE_BAR_MAX_TRACK_PERCENT;
};

const getScoreFractionDigits = (value: ScoreValue, fallback = 1): number =>
  typeof value === 'number' && Number.isFinite(value) && value % 1 === 0
    ? 0
    : fallback;

const hasScoreValue = (value: ScoreValue | undefined): value is number =>
  value !== null && Number.isFinite(value);

/** Section 2: Enterprise score (Figma 3144:4069 / mobile 3154:4161). */
const EnterpriseScoreSection: React.FC<EnterpriseScoreSectionProps> = ({
  data,
  isOpeningStockReport = false,
  onOpenStockReport,
}) => {
  const shouldFetchReportLink = Boolean(onOpenStockReport);
  const hasReportLink = shouldFetchReportLink || data.stockReportUrl !== undefined;
  const reportHref = data.stockReportUrl ? data.stockReportUrl : '#';
  const isPlaceholder = !shouldFetchReportLink && !data.stockReportUrl;

  const handleReportClick = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (shouldFetchReportLink) {
      event.preventDefault();
      if (!onOpenStockReport || isOpeningStockReport) return;

      const reportWindow = window.open('', '_blank');
      if (reportWindow) {
        reportWindow.opener = null;
      }

      try {
        const pdfLink = await onOpenStockReport();
        if (reportWindow) {
          reportWindow.location.href = pdfLink;
        } else {
          window.open(pdfLink, '_blank', 'noopener,noreferrer');
        }
      } catch {
        reportWindow?.close();
      }
      return;
    }

    if (isPlaceholder) {
      event.preventDefault();
    }
  };

  return (
    <section className="summary-section enterprise-score" data-node-id="3144:4069">
      <SectionHeader title="企業スコア" updateDate={`更新日：${formatUpdateDate(data.updateDate)}`} showHelp />
      <div className="enterprise-score__panel">
        <div className="enterprise-score__content-row">
          <div className="enterprise-score__score-table" aria-label="企業スコア" data-node-id="3484:25701">
            <div className="enterprise-score__table-row enterprise-score__table-row--total">
              <div className="enterprise-score__label-cell">企業スコア総合</div>
              <div className="enterprise-score__value-cell">
                <strong>{formatNumber(data.totalScore, 0)}</strong>
                <span> (業界平均値 {formatNumber(data.industryAverage, 1)})</span>
              </div>
            </div>
            {data.dimensions.map((dimension) => (
              <div key={dimension.key} className="enterprise-score__table-row">
                <div className="enterprise-score__label-cell">
                  {dimension.tableLabel ?? dimension.label}
                </div>
                <div className="enterprise-score__value-cell enterprise-score__metric-cell">
                  <div className="enterprise-score__metric-line">
                    {hasScoreValue(dimension.companyValue) && (
                      <span
                        className="enterprise-score__bar enterprise-score__bar--company"
                        style={{ width: `${barWidthPercent(dimension.companyValue, data.axisMax)}%` }}
                      />
                    )}
                    <span className="enterprise-score__metric-value">
                      {formatNumber(
                        dimension.companyValue,
                        getScoreFractionDigits(dimension.companyValue),
                      )}
                    </span>
                  </div>
                  <div className="enterprise-score__metric-line">
                    {hasScoreValue(dimension.industryValue) && (
                      <span
                        className="enterprise-score__bar enterprise-score__bar--industry"
                        style={{ width: `${barWidthPercent(dimension.industryValue, data.axisMax)}%` }}
                      />
                    )}
                    <span className="enterprise-score__industry-value">
                      ({formatNumber(dimension.industryValue, 1)})
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="enterprise-score__chart" data-node-id="3484:25182">
            <ScoreRadarChart
              dimensions={data.dimensions}
              axisMax={data.axisMax}
              height={SCORE_RADAR_CANVAS_HEIGHT}
            />
          </div>
        </div>
        {(data.outlookTitle || data.outlookText) && (
          <p className="enterprise-score__outlook">
            {data.outlookTitle && (
              <strong className="enterprise-score__outlook-title">{data.outlookTitle}</strong>
            )}
            {data.outlookText && (
              <span className="enterprise-score__outlook-body">{data.outlookText}</span>
            )}
          </p>
        )}
        {hasReportLink && (
          <a
            className="enterprise-score__report-link"
            href={reportHref}
            aria-label="株式レポートを開く"
            aria-disabled={isOpeningStockReport || undefined}
            onClick={handleReportClick}
            {...(shouldFetchReportLink || isPlaceholder
              ? {}
              : { target: '_blank', rel: 'noopener noreferrer' })}
          >
            株式レポートはこちら
          </a>
        )}
      </div>
    </section>
  );
};

export default EnterpriseScoreSection;
